/* ─────────────────────────────────────────────────────────────
   gpc-guide.js
   Upload to: Assets/

   Behaviour for the "How to use the Configurator" page:
     • header contents list, built from the sections on the page
     • click-to-load YouTube/Vimeo embeds in any media frame
     • the sticky "Your build" panel that fills as the steps scroll
     • the colour-filter and price-difference demos inside steps
     • the compatibility lab and the fan package calculator
     • the launch pad's live URL

   Loaded GLOBALLY from snippets/head.liquid — a <script> inside a
   section never runs on this storefront. Every part exits at once on
   pages where its markup is absent.
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(n, cur) {
    cur = cur || '£';
    return cur + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function parseJSON(el, what) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) {
      if (window.console) console.error('[gpc-guide] could not read the ' + (what || 'data') + ' payload.', e);
      return null;
    }
  }
  function ready(el, key) {
    if (el.dataset[key] === '1') return false;
    el.dataset[key] = '1';
    return true;
  }

  /* ── contents list: one entry per section that declares a title ── */
  function initToc(root) {
    // The ready flag must NOT be 'gpgToc': that writes data-gpg-toc="1" onto
    // the root, which then matches the item selector below and lists itself.
    if (!ready(root, 'gpgTocDone')) return;
    var list = root.querySelector('ol');
    var items = [].slice.call(document.querySelectorAll('[data-gpg-toc]')).filter(function (el) { return el !== root; });
    if (!list || !items.length) { root.hidden = true; return; }
    list.innerHTML = items.map(function (el) {
      var mark = el.getAttribute('data-gpg-toc-mark') || '—';
      var owner = el.id ? el : el.closest('[id]');
      if (!owner) return '';
      return '<li><a href="#' + esc(owner.id) + '"><i>' + esc(mark) + '</i>' + esc(el.getAttribute('data-gpg-toc')) + '</a></li>';
    }).join('');
  }

  /* ── click-to-load embeds ── */
  function initEmbeds(scope) {
    scope.querySelectorAll('[data-gpg-embed]').forEach(function (el) {
      if (!ready(el, 'gpgEmbed')) return;
      el.addEventListener('click', function () {
        var src = el.getAttribute('data-src');
        if (!src || el.classList.contains('is-playing')) return;
        var f = document.createElement('iframe');
        f.src = src; f.title = el.getAttribute('data-title') || 'Video';
        f.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        f.setAttribute('allowfullscreen', '');
        el.innerHTML = ''; el.appendChild(f); el.classList.add('is-playing');
      });
    });
  }

  /* ── steps: the sticky panel ── */
  function initSteps(root) {
    if (!ready(root, 'gpgSteps')) return;
    var chaps = [].slice.call(root.querySelectorAll('[data-gpg-chap]'));
    var slots = root.querySelector('[data-gpg-slots]');
    var btot = root.querySelector('[data-gpg-btot]');
    var prog = root.querySelector('[data-gpg-prog]');
    var bpow = root.querySelector('[data-gpg-bpow]');
    var blead = root.querySelector('[data-gpg-blead]');
    var bcode = root.querySelector('[data-gpg-bcode]');
    var cur = root.dataset.currency || '£';
    var powNote = root.dataset.powNote || 'Power estimate appears at the GPU';
    var leadNote = root.dataset.leadNote || '';
    var codeDemo = root.dataset.codeDemo || '7X2KQ9AB';
    if (!chaps.length || !slots) return;

    // build the slot list from the chapters
    slots.innerHTML = chaps.map(function (c) {
      var n = c.dataset.num || '0';
      return '<div class="gpg-slot" data-k="' + esc(c.dataset.key) + '">' +
        '<span class="gpg-slot__n">' + esc(n) + '</span>' +
        '<span class="gpg-slot__lab"><small>' + esc(c.dataset.slotLabel || c.dataset.title || '') + '</small><b>—</b></span>' +
        '<span class="gpg-slot__pr">—</span></div>';
    }).join('');

    var seen = {};
    function paint(active) {
      var sum = 0, done = 0, pow = 0;
      chaps.forEach(function (c) {
        var k = c.dataset.key, s = slots.querySelector('[data-k="' + k + '"]');
        var on = !!seen[k];
        s.classList.toggle('is-on', on); s.classList.toggle('is-act', k === active);
        var b = s.querySelector('.gpg-slot__lab b'), pr = s.querySelector('.gpg-slot__pr');
        if (on) {
          done++;
          b.textContent = c.dataset.part || '—';
          var price = parseFloat(c.dataset.price) || 0;
          if (c.dataset.note) { pr.textContent = c.dataset.note; pr.classList.toggle('is-inc', /^included$/i.test(c.dataset.note)); }
          else { pr.textContent = price ? money(price, cur) : '—'; pr.classList.remove('is-inc'); }
          sum += price;
          if (c.dataset.pow) pow = +c.dataset.pow;
        } else { b.textContent = '—'; pr.textContent = '—'; pr.classList.remove('is-inc'); }
      });
      if (btot) btot.textContent = money(sum, cur);
      if (prog) prog.style.width = Math.round(done / chaps.length * 100) + '%';
      if (bpow) bpow.textContent = pow ? ('~' + pow + 'W est. draw · min ' + Math.round(pow * 1.17 / 10) * 10 + 'W PSU') : powNote;
      if (blead) blead.textContent = done > 2 ? leadNote : '';
      if (bcode) bcode.textContent = done === chaps.length ? codeDemo : '––––––––';
    }

    if (!('IntersectionObserver' in window) || reduce) {
      chaps.forEach(function (c) { seen[c.dataset.key] = true; });
      paint(null);
      return;
    }
    var io = new IntersectionObserver(function (es) {
      var active = null;
      es.forEach(function (e) { if (e.isIntersecting) { seen[e.target.dataset.key] = true; active = e.target.dataset.key; } });
      // fill everything up to the furthest chapter reached, so a fast scroll still assembles in order
      var far = -1;
      chaps.forEach(function (c, i) { if (seen[c.dataset.key]) far = Math.max(far, i); });
      chaps.forEach(function (c, i) { if (i <= far) seen[c.dataset.key] = true; });
      paint(active || (far >= 0 ? chaps[far].dataset.key : null));
    }, { rootMargin: '-35% 0px -45% 0px', threshold: 0 });
    chaps.forEach(function (c) { io.observe(c); });
    paint(null);
  }

  /* ── step demos ── */
  function initColourDemo(root) {
    if (!ready(root, 'gpgColour')) return;
    var seg = root.querySelector('.gpg-seg'), parts = [].slice.call(root.querySelectorAll('.gpg-part'));
    function apply(c) { parts.forEach(function (p) { p.classList.toggle('is-hid', c !== 'both' && p.dataset.c !== c); }); }
    seg.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      seg.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
      apply(b.dataset.c);
    });
    var on = seg.querySelector('[aria-pressed="true"]');
    apply(on ? on.dataset.c : 'both');
  }
  function initPriceDemo(root) {
    if (!ready(root, 'gpgPrice')) return;
    var cur = root.dataset.currency || '£';
    function paint() {
      var sel = root.querySelector('.gpg-opt.is-sel'); if (!sel) return;
      var base = parseFloat(sel.dataset.p) || 0;
      root.querySelectorAll('.gpg-opt').forEach(function (o) {
        var p = parseFloat(o.dataset.p) || 0, pr = o.querySelector('.gpg-opt__pr'), d = p - base;
        pr.className = 'gpg-opt__pr';
        if (o === sel) pr.textContent = money(p, cur);
        else { pr.textContent = (d > 0 ? '+' : '−') + money(Math.abs(d), cur); pr.classList.add(d > 0 ? 'is-up' : 'is-dn'); }
      });
    }
    root.addEventListener('click', function (e) {
      var o = e.target.closest('.gpg-opt'); if (!o) return;
      root.querySelectorAll('.gpg-opt').forEach(function (x) { x.classList.toggle('is-sel', x === o); });
      paint();
    });
    paint();
  }

  /* ── compatibility lab ── */
  function initCompat(root) {
    if (!ready(root, 'gpgCompat')) return;
    var cases = parseJSON(root.querySelector('[data-gpg-cases]'), 'cases') || [];
    var gpus = parseJSON(root.querySelector('[data-gpg-gpus]'), 'gpus') || [];
    var caseBox = root.querySelector('[data-gpg-casebox]');
    var ok = root.querySelector('[data-gpg-ok]'), no = root.querySelector('[data-gpg-no]');
    var okc = root.querySelector('[data-gpg-okc]'), noc = root.querySelector('[data-gpg-noc]');
    var why = root.querySelector('[data-gpg-why]'), whycase = root.querySelector('[data-gpg-whycase]');
    var pow = root.querySelector('[data-gpg-pow]');
    var t = { fits: root.dataset.tFits || 'Fits', tooLong: root.dataset.tTooLong || 'Too long', min: root.dataset.tMin || 'min', psu: root.dataset.tPsu || 'PSU' };
    if (!cases.length || !gpus.length || !caseBox) return;
    var ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="5" y="3" width="14" height="18" rx="1.5"/><path d="M8 7h8M8 10h8"/></svg>';
    caseBox.innerHTML = cases.map(function (c, i) {
      return '<button type="button" class="gpg-case" data-i="' + i + '" aria-pressed="' + (i === 0) + '">' + ICON +
        '<span><b>' + esc(c.n) + '</b><small>' + esc(c.sub || ('GPU clearance ' + c.l + ' mm')) + '</small></span></button>';
    }).join('');
    function row(g, fits) {
      return '<div class="gpg-gpu ' + (fits ? 'is-ok' : 'is-no') + '"><span>' + esc(g.n) + '</span><span class="gpg-gpu__len">' + esc(g.l) + ' mm</span><span class="gpg-gpu__st">' + (fits ? t.fits : t.tooLong) + '</span></div>';
    }
    function paint() {
      var b = caseBox.querySelector('[aria-pressed="true"]'), c = cases[+b.dataset.i];
      var f = gpus.filter(function (g) { return +g.l <= +c.l; }), h = gpus.filter(function (g) { return +g.l > +c.l; });
      if (ok) ok.innerHTML = f.map(function (g) { return row(g, true); }).join('');
      if (no) no.innerHTML = h.map(function (g) { return row(g, false); }).join('');
      if (okc) okc.textContent = f.length; if (noc) noc.textContent = h.length;
      if (why) why.hidden = !h.length; if (whycase) whycase.textContent = c.n;
      if (pow && f.length) { var top = f[f.length - 1], est = 300 + (+top.w || 0); pow.innerHTML = '~' + est + 'W · <em>' + t.min + ' ' + Math.round(est * 1.17 / 10) * 10 + 'W ' + t.psu + '</em>'; }
    }
    caseBox.addEventListener('click', function (e) {
      var b = e.target.closest('.gpg-case'); if (!b) return;
      caseBox.querySelectorAll('.gpg-case').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
      paint();
    });
    paint();
  }

  /* ── fan package calculator ── */
  function initFans(root) {
    if (!ready(root, 'gpgFans')) return;
    var CASES = parseJSON(root.querySelector('[data-gpg-fcases]'), 'fan cases') || [];
    var FAMS = parseJSON(root.querySelector('[data-gpg-ffams]'), 'fan families') || [];
    var g = root.querySelector('[data-gpg-fslots]'), cap = root.querySelector('[data-gpg-fcaption]');
    var caseSeg = root.querySelector('[data-gpg-fcase]'), coolSeg = root.querySelector('[data-gpg-fcool]');
    var famBox = root.querySelector('[data-gpg-ffam]'), price = root.querySelector('[data-gpg-fprice]');
    var expl = root.querySelector('[data-gpg-fexpl]'), toast = root.querySelector('[data-gpg-ftoast]');
    var cur = root.dataset.currency || '£';
    var t = { inc: root.dataset.tIncluded || 'Included', perFan: root.dataset.tPerFan || '/fan',
              removed: root.dataset.tRemoved || 'Your fan package was removed: {fam} can\'t fill the {case} ({why}). Switched to {alt}.',
              whyRev: root.dataset.tWhyRev || 'needs reverse-blade fans', why140: root.dataset.tWhy140 || 'it has a 140mm slot',
              reqd: root.dataset.tRequired || 'This case ships with no fans, so a package is required before checkout.' };
    if (!CASES.length || !FAMS.length || !g) return;

    var caseI = 0, cool = 'air', fam = 0, first = true;
    caseSeg.innerHTML = CASES.map(function (c, i) { return '<button type="button" data-i="' + i + '" aria-pressed="' + (i === 0) + '">' + esc(c.short || c.n) + '</button>'; }).join('');
    // slot layouts by count: [x,y] positions on a 120x120 grid
    var LAYOUT = {
      top: [[14,14],[38,14],[62,14]], side: [[86,26],[86,50],[86,74]], bottom: [[14,86],[38,86],[62,86]], extra: [[14,50],[86,14]]
    };
    function slotsFor(c) {
      // c.top / c.side / c.bottom = counts; c.topRad = true if the top row takes the radiator; c.ships = fans in the box
      var out = [], k;
      for (k = 0; k < Math.min(c.top || 0, 3); k++) out.push({ x: LAYOUT.top[k][0], y: LAYOUT.top[k][1], rad: !!c.topRad });
      for (k = 0; k < Math.min(c.side || 0, 3); k++) out.push({ x: LAYOUT.side[k][0], y: LAYOUT.side[k][1], rad: false });
      for (k = 0; k < Math.min(c.bottom || 0, 3); k++) out.push({ x: LAYOUT.bottom[k][0], y: LAYOUT.bottom[k][1], rad: false });
      // ships-with fans fill non-radiator slots first
      var ships = +c.ships || 0;
      out.forEach(function (s) { if (!s.rad && ships > 0) { s.own = true; ships--; } });
      out.forEach(function (s) { if (s.rad && ships > 0) { s.own = true; ships--; } });
      return out;
    }
    function avail(f, c) {
      if (c.has140 && !f.has140) return false;
      if (c.reverse && !f.reverse) return false;
      return true;
    }
    function paint() {
      var c = CASES[caseI], aio = cool === 'aio', slots = slotsFor(c);
      g.innerHTML = slots.map(function (s) {
        var fill = s.own ? '#1f9d5b' : '#b4530a';
        if (s.rad && aio) fill = '#2f6bed';
        return '<rect class="gpg-slotc" x="' + s.x + '" y="' + s.y + '" width="20" height="20" rx="4" fill="' + fill + '"/>';
      }).join('');
      var need = slots.length;
      var rad = aio ? slots.filter(function (s) { return s.rad; }).length : 0;
      var credited = slots.filter(function (s) { return s.own && !(aio && s.rad); }).length;
      var toBuy = Math.max(0, need - rad - credited);
      var was = fam;
      famBox.innerHTML = FAMS.map(function (f, i) {
        var a = avail(f, c);
        return '<button type="button" data-i="' + i + '" aria-pressed="' + (i === fam) + '"' + (a ? '' : ' disabled') + '><span>' + esc(f.n) + '</span><span class="gpg-fam__pr">' + money(f.per, cur) + t.perFan + '</span></button>';
      }).join('');
      var cf = FAMS[fam];
      if (!avail(cf, c)) {
        var altI = FAMS.findIndex(function (f) { return avail(f, c); });
        if (altI < 0) altI = 0;
        fam = altI; cf = FAMS[fam];
        var msg = t.removed.replace('{fam}', FAMS[was].n).replace('{case}', c.n).replace('{why}', c.reverse ? t.whyRev : t.why140).replace('{alt}', cf.n);
        if (toast) { toast.textContent = msg; toast.classList.toggle('is-show', !first); }
        famBox.querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-pressed', String(+b.dataset.i === fam)); });
      } else if (toast) toast.classList.remove('is-show');
      var total = toBuy * (+cf.per || 0);
      if (price) { price.textContent = total === 0 ? t.inc : money(total, cur); price.classList.toggle('is-inc', total === 0); }
      if (expl) {
        expl.textContent = need + ' fan slots' + (rad ? ', ' + rad + ' taken by the radiator' : '') + (credited ? ', ' + credited + ' already ship with the case' : '') +
          ' → ' + toBuy + ' to buy' + (toBuy ? ' × ' + money(cf.per, cur) : '') + '.' + ((+c.ships || 0) === 0 && toBuy ? ' ' + t.reqd : '');
      }
      if (cap) cap.textContent = c.n;
      first = false;
    }
    caseSeg.addEventListener('click', function (e) { var b = e.target.closest('button'); if (!b) return; caseI = +b.dataset.i; caseSeg.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); }); paint(); });
    coolSeg.addEventListener('click', function (e) { var b = e.target.closest('button'); if (!b) return; cool = b.dataset.k; coolSeg.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); }); paint(); });
    famBox.addEventListener('click', function (e) { var b = e.target.closest('button'); if (!b || b.disabled) return; fam = +b.dataset.i; paint(); });
    paint();
  }

  /* ── launch pad ── */
  function initLaunch(root) {
    if (!ready(root, 'gpgLaunch')) return;
    var modes = root.querySelector('[data-gpg-modes]'), pre = root.querySelector('[data-gpg-pre]');
    var bc = root.querySelector('[data-gpg-code]'), url = root.querySelector('[data-gpg-url]'), go = root.querySelector('[data-gpg-go]');
    var base = root.dataset.base || '/apps/configurator';
    if (!modes || !url || !go) return;
    function paint() {
      var mb = modes.querySelector('.gpg-mode[aria-pressed="true"]'), m = mb ? mb.dataset.m : 'scratch', u = base;
      if (m === 'system' && pre) { var s = pre.querySelector('[aria-pressed="true"]'); if (s && s.dataset.sku) u += '?system=' + encodeURIComponent(s.dataset.sku); }
      if (m === 'build' && bc) { var v = bc.value.trim(); u += '?build=' + (v ? encodeURIComponent(v) : '…'); }
      url.textContent = u; go.setAttribute('href', u.replace('…', ''));
    }
    // The mode cards are role="button" divs, not <button>s: a button cannot
    // legally contain the prebuilt buttons or the code input, and the parser
    // silently closes it early, which is why ?system= never made it into
    // the URL in the first harness run. Divs need the keyboard wired by hand.
    modes.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var m = e.target.closest('.gpg-mode'); if (!m || e.target !== m) return;
      e.preventDefault(); m.click();
    });
    modes.addEventListener('click', function (e) {
      var pb = e.target.closest('[data-gpg-pre] button');
      if (pb) { pre.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', String(x === pb)); }); paint(); return; }
      if (e.target.closest('input')) return;
      var m = e.target.closest('.gpg-mode'); if (!m) return;
      modes.querySelectorAll('.gpg-mode').forEach(function (x) { x.setAttribute('aria-pressed', String(x === m)); });
      paint();
      if (m.dataset.m === 'build' && bc) bc.focus();
    });
    if (bc) bc.addEventListener('input', paint);
    paint();
  }

  /* ── boot ── */
  function initAll(scope) {
    scope = scope || document;
    initEmbeds(scope);
    scope.querySelectorAll('[data-gpg-toc-root]').forEach(initToc);
    scope.querySelectorAll('[data-gpg-steps]').forEach(initSteps);
    scope.querySelectorAll('[data-gpg-demo="colour"]').forEach(initColourDemo);
    scope.querySelectorAll('[data-gpg-demo="price"]').forEach(initPriceDemo);
    scope.querySelectorAll('[data-gpg-compat]').forEach(initCompat);
    scope.querySelectorAll('[data-gpg-fans]').forEach(initFans);
    scope.querySelectorAll('[data-gpg-launch]').forEach(initLaunch);
  }

  window.GPC_GUIDE_VERSION = '2026-08-16 guide v1';

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  window.addEventListener('load', function () { initAll(); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
